import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/icons.js';
import { StatusPill, TechAvatar, fmtDate, fmtTime } from '../components/shell.js';
import { clients, jobs, techs, STATUS_FLOW, type JobStatus } from '../data/seed.js';

type StatusFilter = 'all' | JobStatus;

export function JobsListPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [techFilter, setTechFilter] = useState<string>('all');
  const clientById = Object.fromEntries(clients.map((c) => [c.id, c]));
  const techById = Object.fromEntries(techs.map((t) => [t.id, t]));

  const STATUSES: StatusFilter[] = ['all', ...STATUS_FLOW, 'Cancelled'];
  const STATUS_COUNT = (s: StatusFilter): number =>
    s === 'all' ? jobs.length : jobs.filter((j) => j.status === s).length;

  const filtered = jobs
    .filter((j) => {
      if (status !== 'all' && j.status !== status) return false;
      if (techFilter !== 'all' && j.techId !== techFilter) return false;
      if (q) {
        const s = q.toLowerCase();
        const c = clientById[j.clientId]!;
        return (
          j.id.toLowerCase().includes(s) ||
          c.name.toLowerCase().includes(s) ||
          j.appliance.type.toLowerCase().includes(s) ||
          j.problem.toLowerCase().includes(s)
        );
      }
      return true;
    })
    .sort((a, b) => new Date(b.scheduledStart).getTime() - new Date(a.scheduledStart).getTime());

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Jobs</h1>
          <p>
            {jobs.length} total ·{' '}
            {
              jobs.filter((j) =>
                ['New', 'Scheduled', 'In Progress', 'Waiting Parts'].includes(j.status),
              ).length
            }{' '}
            active
          </p>
        </div>
        <div className="page-header-actions">
          <button className="btn primary">{Icon.plus({ size: 14 })} New job</button>
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-wrap">
          {Icon.search({ size: 14 })}
          <input
            className="input"
            placeholder="Search by ID, client, appliance…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          className="select"
          style={{ width: 'auto' }}
          value={techFilter}
          onChange={(e) => setTechFilter(e.target.value)}
        >
          <option value="all">All techs</option>
          {techs.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-bar" style={{ marginBottom: 14 }}>
        {STATUSES.map((s) => (
          <button
            key={s}
            className={'chip ' + (status === s ? 'active' : '')}
            onClick={() => setStatus(s)}
          >
            {s === 'all' ? 'All' : s}{' '}
            <span className="muted" style={{ marginLeft: 4 }}>
              {STATUS_COUNT(s)}
            </span>
          </button>
        ))}
      </div>

      {/* Desktop table */}
      <div className="table-wrap hidden-mobile">
        <table className="t">
          <thead>
            <tr>
              <th>Job ID</th>
              <th>Client</th>
              <th>Appliance</th>
              <th>Scheduled</th>
              <th>Tech</th>
              <th>Source</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((j) => {
              const c = clientById[j.clientId]!;
              return (
                <tr key={j.id} onClick={() => navigate('/jobs/' + j.id)}>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {j.id}
                  </td>
                  <td style={{ fontWeight: 500 }}>{c.name}</td>
                  <td>
                    {j.appliance.type} <span className="muted">· {j.appliance.brand}</span>
                  </td>
                  <td style={{ fontSize: 13 }}>
                    <div>{fmtDate(j.scheduledStart)}</div>
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      {fmtTime(j.scheduledStart)}
                    </div>
                  </td>
                  <td>{techById[j.techId] && <TechAvatar tech={techById[j.techId]} size="sm" />}</td>
                  <td style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
                    {j.source}
                  </td>
                  <td>
                    <StatusPill status={j.status} />
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="empty-state">
                  No jobs match those filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="card-list only-mobile">
        {filtered.map((j) => {
          const c = clientById[j.clientId]!;
          return (
            <div className="list-card" key={j.id} onClick={() => navigate('/jobs/' + j.id)}>
              <div className="list-card-row">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    className="mono"
                    style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))' }}
                  >
                    {j.id}
                  </div>
                  <div className="list-card-title" style={{ marginTop: 2 }}>
                    {c.name}
                  </div>
                  <div className="list-card-sub">
                    {j.appliance.type} · {j.appliance.brand}
                  </div>
                </div>
                <StatusPill status={j.status} />
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 10,
                  fontSize: 12,
                  color: 'hsl(var(--muted-foreground))',
                }}
              >
                {Icon.clock({ size: 12 })}
                <span>
                  {fmtDate(j.scheduledStart)} · {fmtTime(j.scheduledStart)}
                </span>
                <span style={{ marginLeft: 'auto' }}>
                  {techById[j.techId] && <TechAvatar tech={techById[j.techId]} size="sm" />}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
